import { Box, Typography } from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";

const livePulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
`;

const skipInk = { shouldForwardProp: (prop: PropertyKey) => prop !== "$ink" };

interface IInkProps {
  $ink?: boolean;
}

export const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const Eyebrow = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: $ink ? "rgba(255, 255, 255, 0.6)" : theme.palette.text.secondary,
}));

export const LiveDot = styled("span")({
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: "#26BD6C",
  animation: `${livePulse} 2s ease-in-out infinite`,

  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
});

export const Cells = styled(
  Box,
  skipInk
)<IInkProps>(({ theme, $ink }) => {
  const hairline = $ink ? "rgba(255, 255, 255, 0.18)" : theme.palette.divider;

  return {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    borderTop: `1px solid ${hairline}`,
    borderBottom: `1px solid ${hairline}`,
    padding: theme.spacing(1.5, 0),
  };
});

export const Cell = styled(
  Box,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0, 1),
  textAlign: "center",

  "& + &": {
    borderLeft: `1px solid ${
      $ink ? "rgba(255, 255, 255, 0.18)" : alpha(theme.palette.divider, 0.6)
    }`,
  },
}));

export const CellValue = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 20,
  fontWeight: 800,
  lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums",
  color: $ink ? theme.palette.common.white : theme.palette.text.primary,
}));

export const CellLabel = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: $ink ? "rgba(255, 255, 255, 0.6)" : theme.palette.text.secondary,
}));
